import { useDeviceStore } from '../../store/device'
import { api, RateLimitError, type CrowdingAggregate } from '../../services/api'
import type { VehiclePosition } from '../../store/transit'
import { storeSelect } from '../../lib/store.svelte'

// Crowdsourced crowding for the vehicle sheet. One piece of shared state (createCrowding)
// and two presentational components, so they can be placed independently:
//   - CrowdingDots.svelte   → public aggregate, beside the route name/heading
//   - CrowdingReport.svelte → rating control, only in the rider panel
// Voting is gated on boarding: someone who merely tapped a train sees the aggregate but
// can't answer, since only a rider on board can report honestly.
//
// confident=true shows N filled dots (+ an optional half) on a 5-dot scale; confident=false
// shows "Not enough reports yet" (the K=3 corroboration gate). The reporter always gets
// immediate local acknowledgement independent of the public aggregate, and a client-side
// cooldown mirrors the server's per-vehicle limit.

export const CROWDING_LEVELS = [
  { level: 1, label: 'Empty' },
  { level: 2, label: 'Light' },
  { level: 3, label: 'Moderate' },
  { level: 4, label: 'Full' },
  { level: 5, label: 'Crushed' },
] as const

// Mirrors the backend's per-device/per-vehicle window. A UX guard only — the server is
// authoritative and 429s if bypassed.
const CLIENT_COOLDOWN_MS = 10 * 60 * 1000

export const CROWDING_DOT_COUNT = 5

export interface CrowdingState {
  readonly aggregate: CrowdingAggregate | null
  readonly loading: boolean
  readonly submitting: boolean
  readonly justReportedLevel: number | null
  readonly error: string | null
  readonly onCooldown: boolean
  readonly noun: string
  onReport: (level: number) => Promise<void>
}

/**
 * Shared state for one vehicle: a single fetch + submit path consumed by both the dots
 * display and the report control, so they stay in sync.
 *
 * Takes a GETTER rather than a vehicle, because the sheet is reused as the selection
 * changes, and keys the fetch off a `$derived` id rather than the object: the object is
 * replaced on every socket push, so an effect reading it directly would refetch each poll.
 */
export function createCrowding(getVehicle: () => VehiclePosition): CrowdingState {
  const device = storeSelect(useDeviceStore, (s) => s.deviceId)

  let aggregate = $state<CrowdingAggregate | null>(null)
  let loading = $state(true)
  let submitting = $state(false)
  let cooldownUntil = $state(0)
  let justReportedLevel = $state<number | null>(null)
  let error = $state<string | null>(null)

  const vehicleId = $derived(getVehicle().vehicleId)
  const noun = $derived(getVehicle().type === 'bus' ? 'bus' : 'car')

  $effect(() => {
    const id = vehicleId
    // A response landing after the sheet moved to a different vehicle, or closed, must not
    // overwrite the newer state.
    let live = true
    loading = true
    api
      .getCrowding(id)
      .then((agg) => { if (live) aggregate = agg })
      .catch(() => {
        /* leave prior aggregate in place; transient fetch failures are non-fatal */
      })
      .finally(() => { if (live) loading = false })
    return () => { live = false }
  })

  async function onReport(level: number) {
    if (submitting || Date.now() < cooldownUntil) return
    const vehicle = getVehicle()
    submitting = true
    error = null
    // Immediate local feedback, independent of the public gate.
    justReportedLevel = level
    try {
      const agg = await api.reportCrowding({
        deviceId: device.value,
        vehicleId: vehicle.vehicleId,
        tripId: vehicle.tripId,
        routeId: vehicle.routeId,
        level,
      })
      aggregate = agg
      cooldownUntil = Date.now() + CLIENT_COOLDOWN_MS
    } catch (err) {
      if (err instanceof RateLimitError) {
        // Server-side guard already fired; respect its window locally.
        cooldownUntil = Date.now() + err.retryAfterSeconds * 1000
        error = 'You already reported recently'
      } else {
        justReportedLevel = null
        error = 'Could not send report'
      }
    } finally {
      submitting = false
    }
  }

  return {
    get aggregate() { return aggregate },
    get loading() { return loading },
    get submitting() { return submitting },
    get justReportedLevel() { return justReportedLevel },
    get error() { return error },
    get onCooldown() { return Date.now() < cooldownUntil },
    get noun() { return noun },
    onReport,
  }
}
